import type { RegisterReadState } from '@repo/front-end-state';
import { isUncatalogued } from '@repo/front-end-utils';

import { type LaunchItem, type LaunchRegisterRow, registerRowItem } from './launchSummaryDetail';

/**
 * When the onboarding Equipment step counts as done — the rule behind its green tick.
 *
 * ## Why this is a module rather than three lines inside `OnboardingPage`
 *
 * It WAS three lines inside `OnboardingPage`, and `generic-match-status` (epic
 * `asset-register-and-library` feature 19, ruling R8) needed to pin it: the whole point of that
 * feature is that placing a generic asset stopped taking the tick out permanently, and a
 * behaviour-changing PR whose headline outcome rests on an untested expression is the shape the
 * charter's §4 gate exists to refuse.
 *
 * `OnboardingPage.tsx` is ~1,900 lines and pulls in Auth0, four stores, the planner and
 * the 3D plan; mounting it to assert an arithmetic predicate is not tractable and the test would
 * characterise the mocks rather than the rule. Lifting the rule out is the smallest change that
 * makes it testable for real, and it is behaviour-preserving by construction — the page now reads
 * these two functions and computes nothing itself.
 *
 * ## The rule, and why each term is there
 *
 * The tick means "the register is ready", and readiness is three claims, each of which has been
 * wrong at some point:
 *
 *  1. **There is a register at all.** An empty register is not complete, it is unstarted. (The
 *     page shows a separate grey "skipped" tick for an operator who moves past deliberately.)
 *  2. **Nothing is still awaiting catalogue resolution.** This reads the REGISTER, not the upload
 *     response — which is what made the generic-placement defect (epic F1) possible.
 *  3. **The last upload in this session rejected no row.** The register alone cannot answer this:
 *     a rejected row simply is not there, so terms 1 and 2 are both satisfied by a HALF-imported
 *     file and the step would tick green while the operator's data was missing. (Multi-agent
 *     review, PR #1136: the prop carrying this answer was never passed, so the term never
 *     reached the predicate.)
 */

/**
 * The subset of a register row this rule reads.
 *
 * The literal union rather than a bare `string`, matching `RegisterAssetRow`,
 * `RegisterListAsset` and the wire's own `TwinAssetMatchStatus`. Optional-plus-null already
 * covers the partial-row case the looseness was for, and `string` bought nothing but the ability
 * to compare against a value that does not exist — on the ONE module extracted specifically to
 * pin this predicate. It also keeps this file inside the grep sweep that finds every site when a
 * status is added — which feature 18 then did, adding `supplied` below.
 */
export interface RegisterCompletionRow extends LaunchRegisterRow {
  match_status?: 'auto' | 'manual' | 'unmatched' | 'supplied' | 'uncatalogued' | null;
}

/**
 * Rows still AWAITING catalogue resolution — the step's "unknown" count.
 *
 * Keyed on the EXPLICIT `'unmatched'`, never on the absence of catalogue text, which is the same
 * predicate `registerToSidebarEntries` and `AssetRegisterList` use and for the same reason: an
 * unmatched row can carry the customer's own "Technogym" in `manufacturer_raw`.
 *
 * **`'generic'` is not counted, and that is this rule's whole correction** (ruling R8, ledger
 * row 33, retired by `generic-source-retirement` row 85). A row created by placing a
 * `twin_generic_asset` from the Library named no catalogue
 * product and never can, so it is not awaiting anything — it is finished. Counting it as unknown
 * took the tick out the moment anyone used the Generic group and left it out **permanently**,
 * because no action existed that could ever resolve those rows. `auto` and `manual` are likewise
 * not counted, and never were.
 *
 * `'supplied'` is likewise not counted (`asset-register-adopt-existing`, ruling O7), and needs no
 * change to the predicate: it is a resolved row carrying a real `reference_id`, taken from a
 * TwinLabs-maintained legacy register rather than derived. Counting it would put the tick out for
 * an adopted twin whose every row is already matched — the same permanent un-tick `generic` caused,
 * with a different cause. A test pins it.
 *
 * `'uncatalogued'` is not counted either (`uncatalogued-terminal-state`, ledger row 119), and this
 * is the case the rule was always meant to cover rather than an exception to it. `'unmatched'` used
 * to mean two things — "the matcher failed and nobody has looked" and "somebody looked and there is
 * nothing to match to" — and the second is loose kit: a barbell set, swiss balls, a prowler sled,
 * which the catalogue cannot describe. Counting those put the tick out for good, with deleting rows
 * the customer owns as the only way back, which Rob declined. A person now records that decision,
 * and the step reads it as decided. No change to the predicate was needed: it already counted only
 * the explicit awaiting state. A test pins it.
 *
 * Absent or null is treated as resolved, deliberately: only an explicit `'unmatched'` means the
 * matcher ran and failed, so a partial row from a narrowed read does not fail the step.
 */
export const isAwaitingResolution = (asset: RegisterCompletionRow): boolean =>
  asset.match_status === 'unmatched';

export const registerRowsAwaitingResolution = (assets: readonly RegisterCompletionRow[]): number =>
  assets.filter(isAwaitingResolution).length;

/**
 * Rows a person has DECIDED have no catalogue product — the Launch summary's third number
 * (`uncatalogued-terminal-state`). Counted separately so "matched" stays a claim about a product:
 * without it `total − awaiting` would report loose kit as matched.
 */
export const registerRowsUncatalogued = (assets: readonly RegisterCompletionRow[]): number =>
  assets.filter(isUncatalogued).length;

/**
 * The Launch summary's three numbers, from one register. Lifted out of `OnboardingPage` so the
 * arithmetic is pinned: `matched` must subtract the uncatalogued rows as well as the unknown ones,
 * or loose kit is counted twice and called matched (review: testing reviewer).
 */
export const launchAssetCounts = (
  assets: readonly RegisterCompletionRow[],
): { matched: number; uncatalogued: number; unknown: number } => {
  const unknown = registerRowsAwaitingResolution(assets);
  const uncatalogued = registerRowsUncatalogued(assets);
  return { matched: assets.length - unknown - uncatalogued, uncatalogued, unknown };
};

/**
 * The rows behind two of those numbers, for the Launch *Equipment/Assets* list
 * (`launch-summary-detail`, R2). The counts above are these lists' lengths, and each list uses the
 * predicate its count always used — `isUncatalogued` and `isAwaitingResolution` — so the number
 * beside a list and the list cannot disagree. Register order is kept.
 */
export const launchAssetLists = (
  assets: readonly RegisterCompletionRow[],
): { uncatalogued: LaunchItem[]; unknown: LaunchItem[] } => ({
  uncatalogued: assets
    .filter(isUncatalogued)
    .map((row, position) => registerRowItem(row, position)),
  unknown: assets
    .filter(isAwaitingResolution)
    .map((row, position) => registerRowItem(row, position)),
});

/**
 * Whether the Equipment step shows its green tick.
 *
 * The third term is the one the register alone cannot supply: a line the importer refused simply is
 * not there, so the other two are both satisfied by a half-written file. It is the latest live
 * import record's `refused` count (`equipment-page-reimport`, R14) — read from the server, so it
 * survives a reload, and cleared by undoing that import or by a newer upload becoming the latest.
 *
 * `'unknown'` while the record is unread or its read failed: the step does not tick, rather than
 * reading "no refusals" from a read that never answered (Failure States). A read that found no live
 * record is 0, so a twin with a good register and no upload history still ticks.
 */
export const equipmentStepComplete = (input: {
  totalAssets: number;
  awaitingResolution: number;
  lateRefusals: number | 'unknown';
}): boolean =>
  input.totalAssets > 0 &&
  input.awaitingResolution === 0 &&
  input.lateRefusals !== 'unknown' &&
  input.lateRefusals === 0;

/**
 * Is the register KNOWN to be empty — as opposed to unread?
 *
 * `registerRows` is `[]` in three different situations and only one of them means "this twin has
 * no assets": before the first read resolves, after a read fails, and after a read that genuinely
 * returned nothing. Every surface that says something affirmative about an empty register has to
 * tell them apart (`wizard-register-on-store`), and before this rule existed they all read
 * `length === 0` and said "empty" for all three.
 *
 * Three surfaces depend on it: the start screen's gate, the grey "skipped" tick its Skip
 * press sets, and the Launch summary's asset counts — the last being the one that matters most,
 * because it is the screen an operator reads immediately before pressing Go Live.
 *
 * `no-twin` counts as not-known: the wizard early-returns without a twin, so it is unreachable
 * there, and a caller that did reach it has no register to describe either way.
 */
export const registerKnownEmpty = (read: RegisterReadState, totalAssets: number): boolean =>
  read === 'ready' && totalAssets === 0;

/**
 * Is the register KNOWN to hold rows? The sibling of `registerKnownEmpty`, for a different
 * consumer: the import controls' disclosure, which opens collapsed on a twin whose register is in.
 *
 * **ROWS ON SCREEN DECIDE IT.** `null` — not yet known — is reserved for the one state where
 * there is genuinely nothing to go on: no rows, and a read that has not settled. That is the whole
 * rule, and it took two corrections to arrive at, both from the same wrong instinct — reading the
 * READ STATE as if it described the register, when what the operator is looking at is the rows.
 *
 * - **A failed read is not unknown.** The store deliberately keeps `registerRows` when a refresh
 *   fails, so the operator is still looking at a real register. Answering `null` there reopened
 *   the import controls over those rows on the one path whose own banner says *do not re-upload* —
 *   and a re-upload of a file with no customer identifier duplicates every row, which is the
 *   failure that banner exists to prevent.
 * - **Nor is a read IN FLIGHT over rows that are still there.** `loadRegisterAssets` sets
 *   `isLoading` without clearing the rows on a same-twin read — the drop fires only on a twin
 *   change — so every after-write refresh spends its whole duration in `loading` with the register
 *   on screen. Read as unknown, that flipped the disclosure open for the length of each read and
 *   shut again after: a flicker on exactly the delete-a-row journey this work exists to smooth.
 * - `no-twin` with no rows has no register at all, so there is nothing to have rows.
 *
 * Both corrections came from Copilot on PR #1762, rounds 1 and 2.
 */
export const registerHasRows = (read: RegisterReadState, totalAssets: number): boolean | null => {
  if (totalAssets > 0) return true;
  return read === 'loading' || read === 'no-twin' ? null : false;
};
